import { SpinWheelMilestoneViewModel } from "@playcharge/common/models/backend/spin-wheel/spin-wheel-milestone-view-model";
import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, effect, inject, Signal, signal } from "@angular/core";
import { SpinWheelStore } from "../../../stores/spin-wheel/spin-wheel.store";
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { SpinWheelForm, SpinWheelMilestoneForm } from "../../../models/spin-wheel/spin-wheel-forms";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { Nullable } from "@playcharge/common/models/backend/nullable";
import { SpinWheelMilestoneRarity } from "@playcharge/common/models/backend/spin-wheel/spin-wheel-milestone-rarity";
import { RewardType } from "@playcharge/common/models/backend/reward-type";
import { EnumKeyValuePipe } from "../../../pipes/enumKeyValue.pipe";
import { ToastrService } from "ngx-toastr";
import { NoScrollDirective } from "../../../directives/noScrollDirective";

@Component({
    selector: "app-spin-wheel",
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatTableModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatButtonModule,
        MatIconModule,
        EnumKeyValuePipe,
        NoScrollDirective,
    ],
    templateUrl: "./spin-wheel.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpinWheelComponent {
    private _spinWheelStore: SpinWheelStore = inject(SpinWheelStore);
    private _toastrService: ToastrService = inject(ToastrService);

    public dataSource: MatTableDataSource<SpinWheelMilestoneViewModel> =
        new MatTableDataSource<SpinWheelMilestoneViewModel>();

    public displayedColumns: Signal<string[]> = signal<string[]>([
        "rarity",
        "rewardType",
        "reward",
        "expReward",
        "dropChance",
        "order",
        "actions",
    ]);

    public form: FormGroup<SpinWheelForm> = new FormGroup<SpinWheelForm>({
        milestones: new FormArray<FormGroup<SpinWheelMilestoneForm>>([]),
    });

    public SpinWheelMilestoneRarity: typeof SpinWheelMilestoneRarity = SpinWheelMilestoneRarity;
    public RewardType: typeof RewardType = RewardType;

    constructor() {
        effect(() => this.spinWheelLoadedEffectFn(), { allowSignalWrites: true });
    }

    public addMilestone(milestone: Nullable<SpinWheelMilestoneViewModel>): void {
        this.form.controls.milestones.push(
            new FormGroup<SpinWheelMilestoneForm>({
                id: new FormControl<number>(milestone?.id ?? 0, {
                    nonNullable: true,
                }),
                rarity: new FormControl<SpinWheelMilestoneRarity>(
                    milestone?.rarity ?? SpinWheelMilestoneRarity.Common,
                    { nonNullable: true },
                ),
                rewardType: new FormControl<RewardType>(
                    milestone?.rewardType ?? RewardType.Currency,
                    { nonNullable: true },
                ),
                reward: new FormControl<number>(milestone?.reward ?? 0, { nonNullable: true }),
                expReward: new FormControl<number>(milestone?.expReward ?? 0, {
                    nonNullable: true,
                }),
                dropChance: new FormControl<number>(milestone?.dropChance ?? null!, {
                    nonNullable: true,
                }),
                order: new FormControl<number>(milestone?.order ?? null!, { nonNullable: true }),
            }),
        );

        this.dataSource.data = this.form.controls.milestones.getRawValue();
    }

    public removeMilestone(index: number): void {
        this.form.controls.milestones.removeAt(index);

        this.dataSource.data = this.form.controls.milestones.getRawValue();
    }

    public updateMilestones(): void {
        const formValue = this.form.getRawValue();

        this._spinWheelStore.updateMilestones(formValue).subscribe({
            next: () => this._toastrService.success("Successfully updated"),
            error: (error) =>
                this._toastrService.error(error.error.description ?? "Internal Error"),
        });
    }

    private spinWheelLoadedEffectFn(): void {
        if (!this.form.controls.milestones.length) {
            for (const milestone of this._spinWheelStore.entities()) {
                this.addMilestone(milestone);
            }
        }
    }
}
